import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Mail, ShieldCheck, Loader2, X } from 'lucide-react';
import { API_BASE } from '@/lib/api-base';
import { useStorefront } from './StorefrontContext';

type SurfaceIntent = 'tracking' | 'booking' | 'rfq' | 'inquiry' | 'chat';

type SurfaceUser = {
  id: string;
  email: string;
  name: string;
  company: string;
  relationshipRole?: string | null;
  whatsappNumber?: string | null;
  marketingConsent?: boolean;
  tenantSlug: string;
  tenantName: string;
};

const RELATIONSHIP_OPTIONS = [
  { value: 'transport', label: 'Transport' },
  { value: 'haulier', label: 'Haulier' },
  { value: 'coloader', label: 'Coloader' },
  { value: 'shipper', label: 'Shipper' },
  { value: 'consignee', label: 'Consignee' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
] as const;

type SurfaceAccessContextValue = {
  accessToken: string | null;
  surfaceUser: SurfaceUser | null;
  ensureAccess: (intent: SurfaceIntent) => Promise<boolean>;
  buildHeaders: () => Record<string, string>;
  clearAccess: () => void;
};

const SurfaceAccessContext = createContext<SurfaceAccessContextValue | null>(null);

function intentLabel(intent: SurfaceIntent): string {
  switch (intent) {
    case 'tracking': return 'Track Booking';
    case 'booking': return 'Book Shipment';
    case 'rfq': return 'Sales Inquiry / RFQ';
    case 'inquiry': return 'Other Inquiry';
    case 'chat': return 'AI Chat';
  }
}

export function SurfaceAccessProvider({ children }: { children: ReactNode }) {
  const { host, experienceMode, brand } = useStorefront();
  const storageKey = `boox_surface_access:${host}`;
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [accessExpiresAt, setAccessExpiresAt] = useState<string | null>(null);
  const [surfaceUser, setSurfaceUser] = useState<SurfaceUser | null>(null);
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState<SurfaceIntent>('tracking');
  const [step, setStep] = useState<'email' | 'profile' | 'verify'>('email');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [relationshipRole, setRelationshipRole] = useState<string>('shipper');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpSentTo, setOtpSentTo] = useState('');
  const [needsProfileStep, setNeedsProfileStep] = useState(false);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  useEffect(() => {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as { accessToken?: string; accessExpiresAt?: string; surfaceUser?: SurfaceUser };
      if (parsed.accessExpiresAt && new Date(parsed.accessExpiresAt).getTime() <= Date.now()) {
        localStorage.removeItem(storageKey);
        return;
      }
      setAccessToken(parsed.accessToken ?? null);
      setAccessExpiresAt(parsed.accessExpiresAt ?? null);
      setSurfaceUser(parsed.surfaceUser ?? null);
      if (parsed.surfaceUser) {
        setName(parsed.surfaceUser.name);
        setCompany(parsed.surfaceUser.company);
        setEmail(parsed.surfaceUser.email);
        setRelationshipRole(parsed.surfaceUser.relationshipRole ?? 'shipper');
        setWhatsappNumber(parsed.surfaceUser.whatsappNumber ?? '');
        setMarketingConsent(parsed.surfaceUser.marketingConsent ?? true);
      }
    } catch {
      localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  const persist = (nextToken: string, nextUser: SurfaceUser, nextExpiresAt: string | null) => {
    setAccessToken(nextToken);
    setAccessExpiresAt(nextExpiresAt);
    setSurfaceUser(nextUser);
    setName(nextUser.name);
    setCompany(nextUser.company);
    setEmail(nextUser.email);
    setRelationshipRole(nextUser.relationshipRole ?? 'shipper');
    setWhatsappNumber(nextUser.whatsappNumber ?? '');
    setMarketingConsent(nextUser.marketingConsent ?? true);
    localStorage.setItem(storageKey, JSON.stringify({ accessToken: nextToken, accessExpiresAt: nextExpiresAt, surfaceUser: nextUser }));
  };

  const clearAccess = () => {
    setAccessToken(null);
    setAccessExpiresAt(null);
    setSurfaceUser(null);
    localStorage.removeItem(storageKey);
  };

  const close = (result: boolean) => {
    setOpen(false);
    setError(null);
    setBusy(false);
    setCode('');
    setOtpSentTo('');
    setNeedsProfileStep(false);
    setStep('email');
    resolverRef.current?.(result);
    resolverRef.current = null;
  };

  const ensureAccess = async (nextIntent: SurfaceIntent) => {
    if (experienceMode === 'portal') return true;
    const isExpired = !!accessExpiresAt && new Date(accessExpiresAt).getTime() <= Date.now();
    if (isExpired) {
      clearAccess();
    }
    if (!isExpired && accessToken && surfaceUser) return true;
    setIntent(nextIntent);
    setOpen(true);
    setStep('email');
    setError(null);
    return await new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  };

  const requestOtp = async (includeProfile: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/selfservice/surface/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host,
          email,
          intent,
          ...(includeProfile ? {
            name,
            company,
            relationshipRole,
            whatsappNumber,
            marketingConsent,
          } : {}),
        }),
      });
      const body = await res.json() as {
        ok: boolean;
        data?: {
          requiresProfile?: boolean;
          isRegistered?: boolean;
          sentTo?: string;
          profile?: {
            name?: string | null;
            company?: string | null;
            relationshipRole?: string | null;
            whatsappNumber?: string | null;
            marketingConsent?: boolean | null;
          };
        };
        error?: { message?: string };
      };
      if (!res.ok || !body.ok) throw new Error(body.error?.message ?? 'Unable to send verification code');
      if (body.data?.requiresProfile) {
        setNeedsProfileStep(true);
        setStep('profile');
        return;
      }
      setNeedsProfileStep(includeProfile);
      if (body.data?.profile) {
        setName(body.data.profile.name ?? '');
        setCompany(body.data.profile.company ?? '');
        setRelationshipRole(body.data.profile.relationshipRole ?? 'shipper');
        setWhatsappNumber(body.data.profile.whatsappNumber ?? '');
        setMarketingConsent(body.data.profile.marketingConsent ?? true);
      }
      setOtpSentTo(body.data?.sentTo ?? email);
      setStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send verification code');
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/selfservice/surface/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host,
          name,
          company,
          email,
          code,
          intent,
          relationshipRole,
          whatsappNumber,
          marketingConsent,
        }),
      });
      const body = await res.json() as {
        ok: boolean;
        data?: { accessToken: string; expiresAt?: string; surfaceUser: SurfaceUser };
        error?: { message?: string };
      };
      if (!res.ok || !body.ok || !body.data) throw new Error(body.error?.message ?? 'Verification failed');
      persist(body.data.accessToken, body.data.surfaceUser, body.data.expiresAt ?? null);
      close(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const value = useMemo<SurfaceAccessContextValue>(() => ({
    accessToken,
    surfaceUser,
    ensureAccess,
    clearAccess,
    buildHeaders: () => (accessToken ? { 'x-surface-access-token': accessToken } : {} as Record<string, string>),
  }), [accessToken, surfaceUser]);

  return (
    <SurfaceAccessContext.Provider value={value}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 px-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                {step === 'verify' ? <ShieldCheck className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-slate-900">{intentLabel(intent)}</h2>
                <p className="text-sm text-slate-500">
                  Continue with your work email to keep requests linked to your company and {brand.brandShortName}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => close(false)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              {step === 'email' && (
                <>
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-600">Work email</span>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none" />
                  </label>
                  <p className="text-xs text-slate-500">
                    If we already know this email, we will send OTP directly. If not, we will ask for a short registration profile once.
                  </p>
                </>
              )}

              {step === 'profile' && (
                <>
                  <div className="rounded-xl border border-brand-100 bg-brand-50 px-3 py-3 text-sm text-slate-700">
                    We do not know <span className="font-medium">{email}</span> yet. Complete this short registration and we will send OTP next.
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-slate-600">Who are you</span>
                      <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none" />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-slate-600">Company</span>
                      <input value={company} onChange={(e) => setCompany(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-600">How do you work with us</span>
                    <select
                      value={relationshipRole}
                      onChange={(e) => setRelationshipRole(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none"
                    >
                      {RELATIONSHIP_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-600">Work email</span>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-600">WhatsApp number (optional)</span>
                    <input value={whatsappNumber} onChange={(e) => setWhatsappNumber(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none" placeholder="+65 9123 4567" />
                  </label>
                  <label className="flex items-start gap-3 rounded-xl border border-slate-200 px-3 py-3 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span>You agree to receive our free offer, industry news and AI trends.</span>
                  </label>
                </>
              )}

              {step === 'verify' && (
                <>
                  <div className="rounded-xl border border-brand-100 bg-brand-50 px-3 py-3 text-sm text-slate-700">
                    Verification code sent to <span className="font-medium">{otpSentTo || email}</span>
                  </div>
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-600">Email OTP</span>
                    <input value={code} onChange={(e) => setCode(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm tracking-[0.3em] focus:border-brand-500 focus:outline-none" placeholder="123456" />
                  </label>
                </>
              )}

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  if (step === 'verify') {
                    setStep(needsProfileStep ? 'profile' : 'email');
                    return;
                  }
                  if (step === 'profile') {
                    setStep('email');
                    return;
                  }
                  close(false);
                }}
                className="text-sm font-medium text-slate-500 hover:text-slate-700"
              >
                {step === 'email' ? 'Cancel' : 'Back'}
              </button>
              <button
                type="button"
                disabled={
                  busy
                  || !email.trim()
                  || (step === 'profile' && (!name.trim() || !company.trim() || !relationshipRole))
                  || (step === 'verify' && !code.trim())
                }
                onClick={() => {
                  if (step === 'email') {
                    void requestOtp(false);
                    return;
                  }
                  if (step === 'profile') {
                    void requestOtp(true);
                    return;
                  }
                  void verifyOtp();
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {step === 'email' ? 'Continue' : step === 'profile' ? 'Send OTP' : 'Verify & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SurfaceAccessContext.Provider>
  );
}

export function useSurfaceAccess(): SurfaceAccessContextValue {
  const ctx = useContext(SurfaceAccessContext);
  if (!ctx) throw new Error('useSurfaceAccess must be used inside <SurfaceAccessProvider>');
  return ctx;
}
